/**
 * 수업 예약 React Query 훅 (instructor-web)
 *
 * 백엔드 `createInstructorSchedule` (requireCenterMembership)와 1:1 contract.
 * 강사는 소속 센터에서 수업을 예약하며, 담당 강사는 자유 지정한다.
 */

import { useMutation } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { getFirebaseFunctions } from "@/config/firebase";

// --- 백엔드 contract ---

/** 예약 회원 1명 (name + mobile = unique key, ticketTitle = 회원 보유 수강권명) */
export interface ScheduleMemberInput {
  name: string;
  mobile: string;
  ticketTitle: string;
}

interface CreateInstructorScheduleRequest {
  centerName: string;
  title: string;
  type: "P" | "G";
  maxTrainee: number;
  /** KST "YYYY-MM-DDTHH:mm" */
  startOn: string;
  /** KST "YYYY-MM-DDTHH:mm" */
  endOn: string;
  staffName: string;
  staffMobile?: string;
  members: ScheduleMemberInput[];
}

interface CreateInstructorScheduleResponse {
  success: true;
  /** 발급된 scheduleId (10자리 이상) */
  scheduleId: number;
}

export interface CreateScheduleParams {
  centerName: string;
  title: string;
  type: "P" | "G";
  maxTrainee: number;
  startOn: string;
  endOn: string;
  staffName: string;
  staffMobile?: string;
  members: ScheduleMemberInput[];
}

/**
 * 수업 예약 mutation.
 *
 * 성공 시 서버에서 발급된 scheduleId를 반환한다. 호출자(모달의 onSuccess)는
 * 모달 close + 성공 안내만 처리한다.
 */
export function useCreateSchedule() {
  return useMutation({
    mutationFn: async (params: CreateScheduleParams) => {
      const fn = httpsCallable<CreateInstructorScheduleRequest, CreateInstructorScheduleResponse>(
        getFirebaseFunctions(),
        "createInstructorSchedule",
      );
      const result = await fn(params);
      return result.data;
    },
  });
}

// --- 수정·예약취소(강사 가능) / 삭제(owner·admin 전용) contract ---

interface ScheduleMutationResponse {
  success: true;
}

export interface UpdateScheduleParams {
  centerId: number;
  scheduleId: number;
  title?: string;
  /** KST "YYYY-MM-DDTHH:mm" */
  startOn?: string;
  endOn?: string;
  staffName?: string;
  staffMobile?: string;
  maxTrainee?: number;
}

export interface CancelBookingParams {
  centerId: number;
  scheduleId: number;
  bookingId: number;
}

export interface DeleteScheduleParams {
  centerId: number;
  scheduleId: number;
}

/**
 * 수업 정보 수정(reschedule). 강사 전용 `updateInstructorSchedule`을 호출하므로 강사도 가능.
 * 예약 회원(bookings)은 변경하지 않는다.
 */
export function useUpdateSchedule() {
  return useMutation({
    mutationFn: async (params: UpdateScheduleParams) => {
      const fn = httpsCallable<UpdateScheduleParams, ScheduleMutationResponse>(
        getFirebaseFunctions(),
        "updateInstructorSchedule",
      );
      return (await fn(params)).data;
    },
  });
}

/** 예약 취소(bookingId 지정). 강사 전용 `cancelInstructorBooking`을 호출하므로 강사도 가능. */
export function useCancelBooking() {
  return useMutation({
    mutationFn: async (params: CancelBookingParams) => {
      const fn = httpsCallable<CancelBookingParams, ScheduleMutationResponse>(
        getFirebaseFunctions(),
        "cancelInstructorBooking",
      );
      return (await fn(params)).data;
    },
  });
}

/**
 * 수업 삭제(소프트). 강사 전용 `deleteInstructorSchedule`(requireAuth + 센터 멤버십)을
 * 호출하므로 강사도 자신이 소속된 센터의 수업을 삭제할 수 있다.
 */
export function useDeleteSchedule() {
  return useMutation({
    mutationFn: async (params: DeleteScheduleParams) => {
      const fn = httpsCallable<DeleteScheduleParams, ScheduleMutationResponse>(
        getFirebaseFunctions(),
        "deleteInstructorSchedule",
      );
      return (await fn(params)).data;
    },
  });
}
